"use client"

import { Text } from "@/elements"
import { motion } from "motion/react"
import Image, { StaticImageData } from "next/image"

import alexErdmann from "@/public/marketing/testimonials/AlexErdmann.png"
import bornxraised from "@/public/marketing/testimonials/BornxRaised.png"
import capitalRecords from "@/public/marketing/testimonials/CapitalRecords.png"
import craftAlan from "@/public/marketing/testimonials/CraftAlan.png"
import jaredCraft from "@/public/marketing/testimonials/JaredCraft.png"
import kevinKloecker from "@/public/marketing/testimonials/KevinKloecker.png"
import rafaelAvcioglu from "@/public/marketing/testimonials/RafaelAvcioglu.png"
import violetSmith from "@/public/marketing/testimonials/VioletSmith.png"
import xulaniAkel from "@/public/marketing/testimonials/XulaniAkel.png"
import clsx from "clsx"

export const Testimonials = () => {
  return (
    <div className="flex flex-col items-center gap-y-12 md:gap-y-20">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col gap-y-2 text-center">
        <Text variant="h4" className="text-marketing-primary-light dark:text-marketing-primary-dark">
          What are people saying
        </Text>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.2,
            },
          },
        }}
        className="flex flex-col flex-wrap gap-5">
        <div className="flex h-full flex-1 flex-col gap-5 md:flex-row">
          <TestimonialCard
            quote="We're shooting 24 campaigns a year now, so I'm having to build 2 crews a month, usually from scratch. We are always looking for upcoming talent who can execute the creative idea well and work within our budget...NOVA is the only place to find those people quickly."
            avatar={alexErdmann}
            name="Alex “2Tone” Erdmann"
            professionString="Born x Raised"
            brandAvatar={bornxraised}
          />
          <TestimonialCard
            quote="Before NOVA, nothing like this by creatives for creatives had ever existed for us. NOVA has genuinely helped keep a roof over my head and keep me plugged into what is going on in this industry! I'm very grateful for it."
            avatar={rafaelAvcioglu}
            name="Rafael Avcioglu"
            professionString="Photographer"
          />
          <TestimonialCard
            quote="NOVA has been a fundamental part of my small business for the last 2 years. NOVA has been my secret weapon to pull off productions of all sizes; and provides a solution to ever changing client needs and requests."
            avatar={jaredCraft}
            name="Jared Craft"
            professionString="Craft Alan"
            brandAvatar={craftAlan}
          />
        </div>
        <div className="hidden h-full flex-row gap-5 md:flex">
          <TestimonialCard
            quote="As a producer, I often need to hire someone within the hour. NOVA is the only app that works as quickly as my mind does."
            avatar={xulaniAkel}
            name="Xulani Akel"
            professionString="Freelance Producer"
          />
          <TestimonialCard
            quote="I have to oversee almost a hundred artists and we have so many projects of varying sizes that we constantly need help executing on. NOVA has been the perfect place for my team and I to do this quickly and be able to trust the talent."
            avatar={kevinKloecker}
            name="Kevin Kloecker"
            professionString="Capital Records"
            brandAvatar={capitalRecords}
          />

          <TestimonialCard
            quote="NOVA has not only given me consistent work to build my portfolio, but it even helped me get the attention of agencies like CAA, and ultimately signed. Thank you, NOVA, for being a comprehensive platform that genuinely helps freelance artists build careers from the ground up."
            avatar={violetSmith}
            name="Violet Smith"
            professionString="Director of Photography"
          />
        </div>
      </motion.div>
    </div>
  )
}

const defaultOptions = {
  reverse: false, // reverse the tilt direction
  max: 25, // max tilt rotation (degrees)
  perspective: 1000, // Transform perspective, the lower the more extreme the tilt gets.
  scale: 1.025, // 2 = 200%, 1.5 = 150%, etc..
  speed: 3000, // Speed of the enter/exit transition
  transition: true, // Set a transition on enter/exit.
  axis: null, // What axis should be disabled. Can be X or Y.
  reset: true, // If the tilt effect has to be reset on exit.
  easing: "cubic-bezier(.03,.98,.52,.99)", // Easing on enter/exit.
}

const TestimonialCard = ({
  quote,
  name,
  professionString,
  avatar,
  brandAvatar,
}: {
  quote: string
  name: string
  professionString: string
  avatar?: StaticImageData
  brandAvatar?: StaticImageData
}) => {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
      }}
      className="flex min-w-0 flex-1 shrink-0 flex-col rounded-xl border border-marketing-background-2-light border-marketing-primary-light/10 bg-marketing-background-2-light p-9 dark:border-marketing-primary-dark/10 dark:bg-marketing-background-2-dark">
      <div className="flex-1">
        <Text className="text-marketing-primary-light dark:text-marketing-primary-dark">{quote}</Text>
      </div>
      <div className="flex items-center gap-x-[10px] pt-6">
        <div className={clsx("relative h-[46px] shrink-0", brandAvatar ? "w-[70px]" : "w-auto")}>
          {avatar ? (
            <Image src={avatar} alt={name} width={176} height={176} className="size-11 rounded-full" />
          ) : (
            <div className="size-11 rounded-full bg-marketing-brand-1" />
          )}
          {brandAvatar ? (
            <Image
              src={brandAvatar}
              width={300}
              height={300}
              alt={"client company logo"}
              className="absolute bottom-0 right-0 size-11 rounded-full border-2 border-marketing-background-1-light dark:border-marketing-background-1-dark"
            />
          ) : null}
        </div>
        <div className="flex flex-col overflow-hidden text-ellipsis">
          <Text className="truncate text-marketing-primary-light dark:text-marketing-primary-dark">{name}</Text>
          <Text className="truncate text-marketing-secondary" variant="label">
            {professionString}
          </Text>
        </div>
      </div>
    </motion.div>
  )
}
